import * as React from "react";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { formatUsd } from "../../utils/formatPrice";

type Size = "sm" | "lg" | "xl";

type Props = {
  size?: Size;
  inverse?: boolean;
  cur?: string;
  unit?: string;
  // Pre-computed USD amount (see utils/usdAmount). When set, a muted
  // "≈ $X USD" suffix renders inline after the unit, on the price baseline.
  // Undefined → no suffix.
  usd?: number;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
};

const SIZE_PX: Record<Size, number> = { sm: 24, lg: 32, xl: 44 };
// Mobile compacts the digit so a 44px `xl` price doesn't dominate a 375px
// column. Internal adaptation — the digit size always tracks the viewport,
// so no call-site ever wants the desktop size on mobile (spec Phase 4).
const SIZE_PX_MOBILE: Record<Size, number> = { sm: 22, lg: 26, xl: 32 };

export function Price({
  size = "lg",
  inverse = false,
  cur = "$",
  unit,
  usd,
  className,
  style,
  children,
}: Props) {
  const isMobile = useBreakpoint() === "mobile";
  const fontSize = (isMobile ? SIZE_PX_MOBILE : SIZE_PX)[size];
  const rootStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "baseline",
    gap: 0,
    // A price is an atomic unit: never let a squeezed flex row crush it and
    // wrap "AUD" / "≈ $X USD" into a vertical stack — the row should wrap
    // around the whole Price instead.
    flexShrink: 0,
    fontFamily: "var(--font-sans)",
    fontWeight: 300,
    fontSize,
    lineHeight: 1,
    letterSpacing: "0.005em",
    color: inverse ? "#fff" : "var(--primary)",
    fontVariantNumeric: "tabular-nums lining-nums",
    ...style,
  };

  const curStyle: React.CSSProperties = {
    fontFamily: "var(--font-sans)",
    fontWeight: 300,
    fontSize: "0.5em",
    color: inverse ? "var(--white-90)" : "var(--primary-60)",
    marginRight: 2,
    position: "relative",
    top: "-0.55em",
  };

  const unitStyle: React.CSSProperties = {
    fontFamily: "var(--font-sans)",
    fontSize: 11,
    fontWeight: "var(--fw-medium)" as React.CSSProperties["fontWeight"],
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    color: inverse ? "var(--white-90)" : "var(--primary-60)",
    marginLeft: 10,
    alignSelf: "flex-end",
    paddingBottom: 4,
  };

  // Inline suffix on the price baseline (the root is align-items: baseline),
  // so the USD figure reads as a muted annotation beside the AUD price rather
  // than a third line that unbalances side-by-side stat rows.
  const usdStyle: React.CSSProperties = {
    fontFamily: "var(--font-sans)",
    fontWeight: 400,
    fontSize: 14,
    letterSpacing: "0.01em",
    color: inverse ? "var(--white-90)" : "var(--primary-60)",
    marginLeft: 14,
    whiteSpace: "nowrap",
  };

  return (
    <span className={className} style={rootStyle}>
      <span style={curStyle}>{cur}</span>
      {children}
      {unit ? <span style={unitStyle}>{unit}</span> : null}
      {usd != null ? <span style={usdStyle}>{formatUsd(usd)}</span> : null}
    </span>
  );
}
